import { motion } from "framer-motion"
import type React from "react"
import { forwardRef, useEffect, useMemo, useState } from "react"
import promptData from "../../data/prompt-examples.json"
import { useTypingAnimation } from "../../hooks/use-typing-animation"
import { LandingIconVerifiedCheck } from "./LandingIconVerifiedCheck"
import { PromptHighlighter } from "./PromptHighlighter"

function LandingSectionBrackyPromptSequence() {
  // Shuffle prompts once per mount
  const prompts = useMemo(() => {
    const arr = [...promptData.prompts]
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      const tempI = arr[i]
      const tempJ = arr[j]
      if (tempI && tempJ) {
        arr[i] = tempJ
        arr[j] = tempI
      }
    }

    // Helper function to detect if a prompt is in English
    const isEnglish = (text: string) => {
      const hasNonEnglishChars = /[¡¿áéíóúñüç买入孟买印第安人队今晚必赢]/i.test(text)
      const startsWithForeignWords = /^(compra|carga|¡cómprame|compa|achetez|买入)/i.test(text)
      return !(hasNonEnglishChars || startsWithForeignWords)
    }

    // Ensure the very first prompt shown is always a "buy" prompt in English
    const firstPrompt = arr[0]
    if (firstPrompt && (firstPrompt.type !== "buy" || !isEnglish(firstPrompt.text))) {
      const firstEnglishBuyIdx = arr.findIndex((p) => p.type === "buy" && isEnglish(p.text))
      if (firstEnglishBuyIdx > 0) {
        const foundPrompt = arr[firstEnglishBuyIdx]
        if (foundPrompt) {
          arr[0] = foundPrompt
          arr[firstEnglishBuyIdx] = firstPrompt
        }
      }
    }
    return arr
  }, [])

  const {
    phase,
    setPhase,
    typedText,
    currentPromptType,
    showCursor,
    handleViewportEnter: handleEnter,
    handleViewportLeave: handleLeave,
  } = useTypingAnimation({ prompts })

  const [fansLiveVisible, setFansLiveVisible] = useState(true)
  const [ellipsisComplete, setEllipsisComplete] = useState(false)
  const [showShimmer, setShowShimmer] = useState(false)

  // Track visibility event from LandingSectionBracky
  useEffect(() => {
    const handler = (evt: Event) => {
      const custom = evt as CustomEvent
      setFansLiveVisible(Boolean(custom.detail?.isVisible))
    }
    window.addEventListener("fansLiveVisible", handler as EventListener)
    return () => window.removeEventListener("fansLiveVisible", handler as EventListener)
  }, [])

  // Listen for ellipsis sequence completion event
  useEffect(() => {
    const handler = () => {
      setEllipsisComplete(true)
    }
    window.addEventListener("ellipsisSequenceComplete", handler)
    return () => window.removeEventListener("ellipsisSequenceComplete", handler)
  }, [])

  // Handle shimmer and phase transitions
  useEffect(() => {
    if (phase === "placeholder") {
      if (!showShimmer) setShowShimmer(true)
      if (fansLiveVisible && ellipsisComplete) {
        setPhase("typing")
      } else if (!fansLiveVisible) {
        setTimeout(() => setPhase("typing"), 250)
      }
    } else if (phase === "typing" && showShimmer) {
      setShowShimmer(false)
    }
  }, [phase, fansLiveVisible, ellipsisComplete, showShimmer, setPhase])

  // viewport handlers
  const handleViewportEnter = () => {
    handleEnter()
    setEllipsisComplete(false)
  }
  const handleViewportLeave = () => handleLeave()

  // cursor color based on prompt type
  const cursorColorClass =
    typedText.length === 0
      ? "text-white/65"
      : currentPromptType === "buy"
        ? "text-[#22FF65]"
        : currentPromptType === "sell"
          ? "text-[#FF2D55]"
          : "text-[#30DDFF]"

  const cursorClasses =
    `${showCursor ? "animate-pulse [animation-duration:1s]" : ""} absolute left-full origin-left ml-[.02rem] -translate-y-[-0.1rem] ${cursorColorClass}`.trim()

  // ------------------- render -------------------
  return (
    <motion.span
      className="inline-block px-1.25"
      onViewportEnter={handleViewportEnter}
      onViewportLeave={handleViewportLeave}
      viewport={{ amount: 0.5 }}
    >
      {phase === "placeholder" ? (
        <span className={`${showShimmer ? "shiny-text-neutral text-shimmer-neutral" : "text-white/50"} opacity-100`}>
          {promptData.placeholder}
        </span>
      ) : (
        <span className="relative text-white/65 bg-clip-text tracking-[-0.02rem]">
          <PromptHighlighter text={typedText} />
          {showCursor && (
            <span
              className={cursorClasses}
              style={{
                transform: "scaleY(1.25)",
                opacity: 0.35,
                transition: "opacity 0.1s ease",
              }}
            >
              │
            </span>
          )}
        </span>
      )}
    </motion.span>
  )
}

// ---------------------------------------------
// Ask Bracky section (extracted from LandingSectionBracky)
// ---------------------------------------------
export const LandingAskBracky = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((_, ref) => (
  <div
    ref={ref as React.Ref<HTMLDivElement>}
    className="relative z-50 px-4 sm:px-0 leading-[1.19] sm:w-3/4 md:w-3/4 lg:w-3/4 sm:mx-auto md:mx-auto lg:mx-auto"
  >
    <h3 className="font-normal text-center tracking-[-0.02rem] -mb-2 pb-[0.2em] text-[1.8rem] sm:text-[1.95rem] md:text-[2.1rem] lg:text-[2.25rem] xl:text-[2.4rem] min-h-40 sm:min-h-36 md:min-h-40 lg:min-h-40 xl:min-h-40 relative z-200">
      {/* Title */}
      <span className="block font-bold mb-[.2rem]">
        <span className="text-white">Ask&nbsp;</span>
        <span className="bg-linear-to-r from-[#0074FF] to-[#30DFFF] bg-clip-text text-transparent inline-flex items-center gap-1 pb-[0.25em] -mb-[0.25em]">
          @bracky
          <LandingIconVerifiedCheck
            className="ml-[-.05rem] w-[1.33rem] h-[1.33rem] translate-y-[.12rem] md:ml-0 md:w-8 md:h-8 md:translate-y-[.2rem]"
            color="#30DDFF"
          />
        </span>
      </span>
      <LandingSectionBrackyPromptSequence />
    </h3>
  </div>
))

LandingAskBracky.displayName = "LandingAskBracky"
